import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPolicies, fetchPoliciesForMultipleUsers, addPolicy } from "../policies/policySlice";
import { useNavigate, useLocation } from "react-router-dom";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import MobileRenewals from './MobileRenewals';
import { firebaseCache } from "../../utils/firebaseCache";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { incrementDailyPolicyCounter } from "../../utils/dailyPolicyCounter";
import { incrementActionCounter } from "../../utils/actionCounters";

// Fallback calendar configuration
const getCalendarConfig = () => {
  try {
    // Try to detect if the system supports the calendar
    const testDate = new Date();
    const testLocale = testDate.toLocaleDateString('en');

    // If basic locale works, use standard configuration
    return {
      calendarType: 'gregory',
      locale: 'en',
      maxDetail: 'month',
      minDetail: 'month'
    };
  } catch (error) {
    console.warn('Calendar locale detection failed, using fallback:', error);
    // Fallback to most basic configuration
    return {
      calendarType: 'gregory',
      locale: undefined,
      maxDetail: 'month',
      minDetail: 'month'
    };
  }
};
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import 'dayjs/locale/en';
import UploadPolicyModal from "../renewals/UploadPolicy";
import UnifiedPolicyForm from "../addPolicy/UnifiedPolicyForm";
import SuccessDialog from "../addPolicy/SuccessDialog";
import RemindDialog from "../../components/common/RemindDialog";
import { COMPANIES } from "../../data/dropdowns";
import ShareIcon from '@mui/icons-material/Share';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShareReportDialog from "./ShareReportDialog";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Link from '@mui/material/Link';
import useMediaQuery from "@mui/material/useMediaQuery";
import Popover from '@mui/material/Popover';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import StatusBadge from "../../components/common/StatusBadge";
import { calculatePolicyStatus, parseDate } from "../../utils/dateUtils";
import { shouldUseMobileLayout } from "../../utils/deviceDetection";

import AddCircleIcon from '@mui/icons-material/AddCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications'
dayjs.extend(weekday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.locale('en');

import { useAdminStaffData } from '../../hooks/useAdminStaffData';
import { useAccountStatus } from '../../hooks/useAccountStatus';

const get = (obj, ...keys) =>
  keys.reduce((acc, k) => (acc !== undefined ? acc : obj?.[k]), undefined);

const Renewals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { policies } = useSelector((state) => state.policies);
  const { user } = useSelector((state) => state.auth);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const { isAccountActive } = useAccountStatus();
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf('week'));
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successPdfUrl, setSuccessPdfUrl] = useState("");
  const [successPremium, setSuccessPremium] = useState(null);
  const [successBalance, setSuccessBalance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [remindDialogOpen, setRemindDialogOpen] = useState(false);
  const [selectedPolicyForRemind, setSelectedPolicyForRemind] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showStatusOptions, setShowStatusOptions] = useState(false);
  const [showCompanyOptions, setShowCompanyOptions] = useState(false);
  const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null); // 'start' or 'end'
  const [showSingleDay, setShowSingleDay] = useState(false);
  const [showShareReport, setShowShareReport] = useState(false);
  const dateRangeCalendarRef = useRef();
  const isTinyScreen = useMediaQuery('(max-height:720px)');  // e.g. 13" laptop
  const veryShortScreen = useMediaQuery('(max-height:770px)');
  const shortScreen = useMediaQuery('(max-height:950px)');
  const mediumScreen = useMediaQuery('(max-height:1025px)');
  const isSmallScreen = useMediaQuery('(max-width:1281px)');
  const isMobile = shouldUseMobileLayout();
  const itemsPerPage = isTinyScreen ? 4 : veryShortScreen ? 4 : shortScreen ? 5 : mediumScreen ? 7 : 9;
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Wait for useAdminStaffData to resolve admin status and staff UIDs
    if (staffLoading) return;

    if (isAdmin && userIds.length > 0) {
      // Admin user - fetch aggregated policies from all linked staff (including admin self)
      console.log('Admin user - fetching aggregated policies for renewals:', userIds);
      dispatch(fetchPoliciesForMultipleUsers(userIds));
    } else if (user?.uid && !isAdmin) {
      // Regular staff user - fetch their own policies ONLY
      console.log('Staff user - fetching own policies for renewals');
      dispatch(fetchPolicies(user.uid));
    }
  }, [userIds, isAdmin, user?.uid, staffLoading, dispatch]);

  // Restore filter state from sessionStorage on mount
  useEffect(() => {
    console.log('🔄 Renewals: Attempting to restore filters from sessionStorage');
    const savedFilters = sessionStorage.getItem('renewals_filterState');
    console.log('📦 Renewals: Saved filters:', savedFilters);
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        console.log('✅ Renewals: Restoring filters:', filters);
        setSelectedCompanies(filters.selectedCompanies || []);
        setSelectedStatuses(filters.selectedStatuses || []);
        setShowCompanyOptions(filters.showCompanyOptions || false);
        setShowStatusOptions(filters.showStatusOptions || false);
        if (filters.dateRange) {
          setDateRange({
            start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
            end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
          });
        }
      } catch (error) {
        console.error('❌ Renewals: Error restoring filter state:', error);
      }
    } else {
      console.log('ℹ️ Renewals: No saved filters found');
    }
    // Delay setting isInitialMount to false to ensure all state updates complete
    setTimeout(() => {
      console.log('✅ Renewals: Restoration complete, enabling filter saves');
      isInitialMount.current = false;
    }, 100);
  }, []); // Run only on mount

  // Restore page and date from navigation state
  useEffect(() => {
    if (location.state?.currentPage) {
      setCurrentPage(location.state.currentPage);
    }
    if (location.state?.selectedDate) {
      setSelectedDate(dayjs(location.state.selectedDate));
    }
  }, [location.state]);

  // Reset to first page when date changes (but not when restoring from navigation state)
  useEffect(() => {
    // Don't reset page if we're restoring from navigation state
    if (location.state?.currentPage && location.state?.selectedDate) {
      console.log('Renewals: Skipping page reset due to navigation state restoration');
      return;
    }
    console.log('Renewals: Resetting page to 1 due to date change');
    setCurrentPage(1);
  }, [selectedDate, location.state]);

  // Save filter state to sessionStorage whenever filters change (but skip if all empty)
  useEffect(() => {
    // Skip saving on initial mount
    if (isInitialMount.current) {
      console.log('⏭️ Renewals: Skipping save on initial mount');
      return;
    }

    // Only save if there are actual filter values (not all empty)
    const hasFilters = selectedCompanies.length > 0 ||
      selectedStatuses.length > 0 ||
      dateRange.start !== null ||
      dateRange.end !== null;

    const filterState = {
      selectedCompanies,
      selectedStatuses,
      showCompanyOptions,
      showStatusOptions,
      dateRange: {
        start: dateRange.start ? dateRange.start.format('YYYY-MM-DD') : null,
        end: dateRange.end ? dateRange.end.format('YYYY-MM-DD') : null
      }
    };

    console.log('💾 Renewals: Saving filters to sessionStorage (hasFilters:', hasFilters, '):', filterState);
    sessionStorage.setItem('renewals_filterState', JSON.stringify(filterState));
  }, [selectedCompanies, selectedStatuses, showCompanyOptions, showStatusOptions, dateRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange.start, dateRange.end, selectedCompanies.join(',')]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(dayjs(date));
    setDateRange({ start: null, end: null });
    setShowSingleDay(true);
    setShowCalendar(false);
  };

  const handleDateRangeCalendarChange = (date) => {
    const selectedDay = dayjs(date);
    if (showDateRangeCalendar === 'start') {
      setDateRange((prev) => ({
        start: selectedDay,
        end: prev.end && selectedDay.isAfter(prev.end) ? null : prev.end
      }));
      setShowDateRangeCalendar('end');
    } else if (showDateRangeCalendar === 'end') {
      // Validation: end date cannot be before start date
      if (selectedDay.isBefore(dateRange.start, 'day')) {
        return;
      }
      setDateRange((prev) => ({
        start: prev.start,
        end: selectedDay
      }));
      setShowDateRangeCalendar(null);
    }
  };

  const handleDayShift = (direction) => {
    if (dateRange.start && dateRange.end) return;
    setSelectedDate((prev) =>
      direction === 'prev' ? prev.subtract(7, 'day').startOf('week') : prev.add(7, 'day').startOf('week')
    );
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleFilterButtonClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleCompanyToggle = (company) => {
    setSelectedCompanies((prev) =>
      prev.includes(company)
        ? prev.filter((c) => c !== company)
        : [...prev, company]
    );
  };

  const handleDateRangeChange = (key, value) => {
    setDateRange((prev) => {
      if (key === 'start') {
        const adjustedEnd = prev.end && value && value.isAfter(prev.end) ? null : prev.end;
        return { start: value, end: adjustedEnd };
      }
      if (key === 'end') {
        if (prev.start && value && value.isBefore(prev.start)) {
          return { start: value, end: value };
        }
        return { start: prev.start, end: value };
      }
      return prev;
    });
  };

  const clearFilters = () => {
    setSelectedCompanies([]);
    setDateRange({ start: null, end: null });
    setSelectedStatuses([]);
    setShowStatusOptions(false);
    setShowCompanyOptions(false);
  };

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const dateRangeActive = Boolean(dateRange.start && dateRange.end);
  const weekEnd = selectedDate.add(6, 'day');

  const getStatusFromExpiry = (expiryDate, originalStatus = null) => {
    return calculatePolicyStatus(expiryDate, originalStatus);
  };

  const companyOptions = useMemo(() => {
    // Load all companies from motor, non_motor, and life categories
    const allCompanies = new Set([
      ...COMPANIES.motor,
      ...COMPANIES.non_motor,
      ...COMPANIES.life
    ]);
    return Array.from(allCompanies).sort((a, b) => a.localeCompare(b));
  }, []);

  const activeFilterCount = selectedCompanies.length + (dateRangeActive ? 1 : 0) + (selectedStatuses.length > 0 ? 1 : 0);

  // Format date to "25 Oct 2025" format
  const formatDateForMessage = (dateInput) => {
    const parsedDate = parseDate(dateInput);
    if (!parsedDate || isNaN(parsedDate.getTime())) return "";
    const day = parsedDate.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[parsedDate.getMonth()];
    const year = parsedDate.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Generate WhatsApp message for renewals
  const generateRenewalMessage = async (policy) => {
    const expiryDate = parseDate(policy.expiryDate);
    if (!expiryDate || isNaN(expiryDate.getTime())) return "";

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);

    const isExpired = today > expiry;
    const formattedDate = formatDateForMessage(policy.expiryDate);
    const policyType = policy.policyType || "Policy";
    const companyName = policy.policyCompany || "your insurer";
    const isMotor = policy.policyCategory === "motor";
    const vehicleMake = policy.vehicleMake || "";
    const vehicleModel = policy.vehicleModel || "";
    const vehicleNumber = policy.vehicleNumber || "";
    const clientName = policy.name || "";
    const greeting = clientName ? `Hi ${clientName}` : "Hi";

    let message = "";

    // Get agent details from Firestore
    let agentName = 'your agent';
    let agentMobile = '';

    try {
      if (user?.uid) {
        const agentDoc = await getDoc(doc(db, 'agent-details', user.uid));
        if (agentDoc.exists()) {
          const agentData = agentDoc.data();
          agentName = agentData.name || agentData.displayName || 'your agent';
          agentMobile = agentData.phone || agentData.phoneNumber || '';
        }
      } else {
        // Fallback to policy data if user is not available
        agentName = policy.agentName || 'your agent';
        agentMobile = policy.agentMobile || '';
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      // Fallback to policy data if there's an error
      agentName = policy.agentName || 'your agent';
      agentMobile = policy.agentMobile || '';
    }

    if (isExpired) {
      // Expired templates
      if (isMotor && vehicleModel && vehicleNumber && vehicleMake) {
        message = `${greeting}\nYour *${policyType} policy* for ${vehicleMake} ${vehicleModel} - *${vehicleNumber}* with ${companyName} expired on *${formattedDate}*⚠️\n\nPlease reply here or give a quick call to your agent: ${agentName} at ${agentMobile} to renew it.`;
      } else if (isMotor && vehicleNumber) {
        message = `${greeting}\nYour *${policyType} policy* , *${vehicleNumber}* with ${companyName} expired on *${formattedDate}*⚠️\n\nPlease reply here or give a quick call to your agent: ${agentName} at ${agentMobile} to renew it.`;
      } else {
        message = `${greeting}\nYour *${policyType}* with ${companyName} expired on *${formattedDate}*⚠️\n\nPlease reply here or give a quick call to your agent: ${agentName} at ${agentMobile} to renew it.`;
      }
    } else {
      // Due for renewal templates
      if (isMotor && vehicleModel && vehicleNumber, vehicleMake) {
        message = `${greeting}\nYour *${policyType} policy* for ${vehicleMake} ${vehicleModel} - *${vehicleNumber}* with ${companyName} is due for renewal on *${formattedDate}* ⏳\n\nPlease reply here or give a quick call to your agent: ${agentName} at ${agentMobile} to renew it before due date`;
      } else if (isMotor && vehicleNumber) {
        message = `${greeting}\nYour *${policyType} policy* , *${vehicleNumber}* with ${companyName} is due for renewal on *${formattedDate}* ⏳\n\nPlease reply here or give a quick call to your agent: ${agentName} at ${agentMobile} to renew it before due date`;
      } else {
        message = `${greeting}\nYour *${policyType}* with ${companyName} is due for renewal on *${formattedDate}* ⏳\n\nPlease reply here or give a quick call to your agent: ${agentName} at ${agentMobile} to renew it before due date`;
      }
    }

    return message;
  };

  // Handle WhatsApp redirect (reuse SuccessDialog's logic)
  const handleWhatsAppRedirect = (message) => {
    if (!message) {
      console.error("No message provided for WhatsApp");
      return;
    }

    const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
    const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`;

    // Try app first using a hidden iframe to avoid console errors
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = whatsappAppUrl;
    document.body.appendChild(iframe);

    // Remove iframe after a short delay
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 100);

    // Listen for page blur (indicates app opened)
    let appOpened = false;
    const handleBlur = () => {
      appOpened = true;
      window.removeEventListener('blur', handleBlur);
    };
    window.addEventListener('blur', handleBlur);

    // Fallback to web after 1.5 seconds ONLY if app didn't open
    setTimeout(() => {
      window.removeEventListener('blur', handleBlur);
      if (!appOpened) {
        window.open(webWhatsappUrl, "_blank", "noopener,noreferrer");
      }
    }, 1500);
  };

  const handleRemindClick = (policy, event) => {
    event.stopPropagation(); // Prevent row click navigation
    setSelectedPolicyForRemind(policy);
    setRemindDialogOpen(true);
  };

  const handleRemindConfirm = async () => {
    if (selectedPolicyForRemind) {
      try {
        const message = await generateRenewalMessage(selectedPolicyForRemind);
        handleWhatsAppRedirect(message);

        if (user?.uid) {
          await incrementActionCounter(user.uid, 'renewalremindercounter');
        }
      } catch (error) {
        console.error('Error generating renewal message:', error);
        // Fallback to a default message if there's an error
        handleWhatsAppRedirect("Your policy is due for renewal. Please contact your agent for more information.");
      }
    }
    setRemindDialogOpen(false);
    setSelectedPolicyForRemind(null);
  };

  const handleRemindClose = () => {
    setRemindDialogOpen(false);
    setSelectedPolicyForRemind(null);
  };

  /**
   * Filter policies based on the selected week or date range
   * Uses the parseDate utility to handle multiple date formats
   */
  const filteredPolicies = useMemo(() => {
    const rangeStart = dateRange.start?.startOf('day');
    const rangeEnd = dateRange.end?.startOf('day');
    const weekStart = selectedDate.startOf('day');
    const weekEndDay = weekEnd.startOf('day');

    return policies
      .filter((policy) => {
        if (!policy.expiryDate) return false;

        const parsedDate = parseDate(policy.expiryDate);
        if (!parsedDate || isNaN(parsedDate.getTime())) return false;

        const expiry = dayjs(parsedDate).startOf('day');
        if (!expiry.isValid()) return false;

        let matchesRange;
        if (rangeStart && rangeEnd) {
          matchesRange = expiry.isSameOrAfter(rangeStart) && expiry.isSameOrBefore(rangeEnd);
        } else if (showSingleDay) {
          matchesRange = expiry.isSame(weekStart);
        } else {
          matchesRange = expiry.isSameOrAfter(weekStart) && expiry.isSameOrBefore(weekEndDay);
        }

        if (!matchesRange) return false;

        if (selectedCompanies.length > 0 && !selectedCompanies.includes(policy.policyCompany)) {
          return false;
        }

        if (selectedStatuses.length > 0) {
          const status = getStatusFromExpiry(policy.expiryDate, policy.status);
          if (!selectedStatuses.includes(status)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        const aDate = dayjs(parseDate(a.expiryDate));
        const bDate = dayjs(parseDate(b.expiryDate));
        if (!aDate.isValid() || !bDate.isValid()) return 0;
        const comparison = aDate.diff(bDate);
        if (comparison === 0) return (a.policyCompany || '').localeCompare(b.policyCompany || '');
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  }, [policies, selectedDate, weekEnd, dateRange.start, dateRange.end, selectedCompanies, selectedStatuses, sortDirection, showSingleDay]);

  const totalPages = Math.ceil(filteredPolicies.length / itemsPerPage) || 1;
  const paginatedPolicies = filteredPolicies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const showExpiryColumn = true; // Always show expiry date
  const showStatusColumn = true; // Always show status column
  const baseColumnCount = 5; // #, Customer, Category, Policy Type, Remind
  const totalColumnCount = baseColumnCount + 2; // +1 for expiry, +1 for status


  return (
    <Box sx={{ px: 2, pt: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{
            fontFamily: 'Roboto Slab',
            fontWeight: 550,
            fontSize: '32px',
            fontStyle: 'Medium',
            lineHeight: '56px',
            letterSpacing: '0.02em',
            opacity: 1,
            width: 244,
            height: 56,
            textEmphasisColor: '#212121'
          }}>
            Policy Renewal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={isAdmin ? <AssessmentIcon /> : <ShareIcon />}
            onClick={() => setShowShareReport(true)}
            sx={{
              width: 160,
              height: 60,
              borderRadius: '8px',
              p: '16px 12px 16px 8px',
              gap: '4px',
              boxShadow: 0,
              backgroundColor: '#E0EBFF !important',
              color: '#0063F7 !important',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '24px',
              letterSpacing: '0.01em',
              '&:hover': {
                backgroundColor: '#E0EBFF !important',
                color: '#0063F7 !important',
                boxShadow: 6
              }
            }}
          >
            {isAdmin ? "View Report" : "Share Report"}
          </Button>
          <Tooltip title={!isAccountActive ? 'Account deactivated. Please contact support to renew your subscription.' : ''} arrow>
            <span>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setShowUploadModal(true)}
                disabled={!isAccountActive}
                sx={{
                  width: 160,
                  height: 60,
                  borderRadius: '8px',
                  p: '16px 12px 16px 8px',
                  gap: '4px',
                  boxShadow: 0,
                  backgroundColor: '#0063F7 !important',
                  color: '#FFFFFF !important',
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0.01em',
                  '&:hover': {
                    backgroundColor: '#0063F7 !important',
                    color: '#FFFFFF !important',
                    boxShadow: 10
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc !important',
                    color: '#888888 !important',
                  }
                }}
              >
                Upload Policy
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{
        width: '100%',
        background: '#fff',
        borderRadius: '12px 12px 0 0',
        boxShadow: 'none',
        px: isMobile ? 0.5 : 3,
        pt: isMobile ? 1 : 2,
        pb: isMobile ? 1 : 3,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        gap: 0,
        minHeight: 'calc(100vh - 120px)', // Ensure container takes full height
        height: 'calc(100vh - 120px)', // Fixed height to prevent shrinking
      }}>
        {/* Top bar: date controls & filters */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: isSmallScreen ? 'nowrap' : 'wrap', gap: isMobile ? 1 : 2, mb: isMobile ? 1.5 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isSmallScreen ? 0.5 : 1.5, position: 'relative', flexWrap: 'nowrap' }}>
            <Button
              onClick={() => {
                setShowCalendar(true);
                setShowSingleDay(false);
              }}
              startIcon={<CalendarMonthIcon />}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: isSmallScreen ? '12px' : '15px',
                lineHeight: '24px',
                letterSpacing: '0.02em',
                color: '#4B5563',
                height: isSmallScreen ? 32 : 40,
                borderRadius: '8px',
                px: isSmallScreen ? 1 : 2,
                textTransform: 'none',
                border: '1px solid #E0E0E0',
                backgroundColor: '#fff',
                '&:hover': {
                  color: '#004DC2',
                  backgroundColor: '#F5F8FF'
                }
              }}
            >
              {selectedDate.format(isSmallScreen ? 'MMM' : 'MMM YYYY')}
            </Button>
            {showCalendar && (
              <Box
                ref={calendarRef}
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 12px)',
                  left: 0,
                  right: 'auto',
                  zIndex: 1400,
                  boxShadow: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  width: 340,
                  overflow: 'hidden',
                  padding: '12px',
                  transform: 'none',
                  '& .react-calendar': {
                    width: '100%',
                    maxWidth: '100%',
                    minHeight: '280px', // Fixed height to prevent jumps
                    '&__navigation': {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0 6px',
                      margin: '0 0 8px 0',
                      '&__label': {
                        minWidth: '180px', // Fixed width for month/year
                        textAlign: 'center',
                        flexGrow: 0,
                        fontWeight: '600 !important',
                        margin: 0,
                        padding: '8px 0',
                        fontSize: '16px',
                        lineHeight: '1.4'
                      },
                      '&__arrow': {
                        minWidth: '32px',
                        padding: '8px 2px',
                        margin: 0
                      }
                    },
                    '&__month-view__weekdays': {
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0 6px',
                      marginBottom: '4px',
                      '&__weekday': {
                        width: '36px',
                        padding: '4px 0',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: '600 !important',
                        textTransform: 'uppercase',
                        color: '#666 !important',
                        backgroundColor: 'transparent !important',
                        border: 'none !important',
                        '& abbr': {
                          textDecoration: 'none',
                          display: 'inline-block',
                          width: '100%',
                          textAlign: 'center'
                        }
                      }
                    },
                    '&__month-view__days': {
                      display: 'grid !important',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '4px',
                      padding: '0 6px',
                      '&__day': {
                        width: '36px',
                        height: '36px',
                        margin: '0 auto',
                        fontWeight: '600 !important',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        '&--neighboringMonth': {
                          opacity: 0.5
                        },
                        '&--weekend': {
                          color: '#212121 !important'
                        },
                        '&--now': {
                          fontWeight: 'bold',
                          color: '#004DC2 !important',
                          backgroundColor: 'transparent !important',
                          '&:enabled:hover': {
                            backgroundColor: '#E0EBFF !important'
                          }
                        }
                      }
                    }
                  },
                  '& .react-calendar__tile': {
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    fontSize: '13px',
                    color: '#212121 !important',
                    transition: 'all 0.2s ease',
                    '&:enabled:hover': {
                      backgroundColor: '#E0EBFF !important',
                      color: '#212121 !important'
                    },
                    '&--active, &--active:enabled, &--active:enabled:hover': {
                      backgroundColor: '#004DC2 !important',
                      color: '#fff !important',
                      borderRadius: '50%',
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none'
                    }
                  },
                  "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled": {
                    backgroundColor: '#004DC2 !important',
                    color: '#fff !important',
                  },
                  "& .react-calendar__month-view__days__day--weekend": {
                    color: '#212121 !important',
                  },
                  "& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover": {
                    backgroundColor: '#E0EBFF !important',
                  },
                  "& .react-calendar__navigation button": {
                    color: '#333 !important',
                    background: 'none !important',
                    fontWeight: '600 !important',
                    border: 'none !important',
                    outline: 'none !important',
                    '&:hover': {
                      backgroundColor: '#E0EBFF !important',
                      color: '#333 !important',
                    },
                    '&:focus': {
                      backgroundColor: 'transparent !important',
                      outline: 'none !important',
                      boxShadow: 'none !important',
                    },
                    '&:active': {
                      backgroundColor: 'transparent !important',
                    },
                  },
                  "& .react-calendar__navigation__label": {
                    color: '#333 !important',
                    background: 'transparent !important',
                    fontWeight: '600 !important',
                    '&:hover': {
                      backgroundColor: 'transparent !important',
                    },
                    '&:focus': {
                      backgroundColor: 'transparent !important',
                      outline: 'none !important',
                    },
                  },
                }}
              >
                {(() => {
                  try {
                    return (
                      <Calendar
                        value={selectedDate.toDate()}
                        onChange={handleDateChange}
                        {...getCalendarConfig()}
                      />
                    );
                  } catch (error) {
                    console.error('Calendar rendering failed:', error);
                    return (
                      <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
                        Calendar could not be loaded. Please try refreshing the page.
                      </Box>
                    );
                  }
                })()}
              </Box>
            )}
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <IconButton
              onClick={() => handleDayShift('prev')}
              disabled={dateRangeActive}
              size="small"
              sx={{
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                width: isSmallScreen ? 28 : 36,
                height: isSmallScreen ? 28 : 36,
                '&:hover': {
                  color: '#004DC2',
                  backgroundColor: '#F5F8FF',
                  borderColor: '#E0E0E0'
                }
              }}
            >
              <Typography component="span" sx={{ fontSize: 20, lineHeight: 1 }}>
                &#8249;
              </Typography>
            </IconButton>
            <Button
              variant="outlined"
              sx={{
                borderRadius: '999px',
                px: isSmallScreen ? 1 : 2.5,
                height: isSmallScreen ? 32 : 40,
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: isSmallScreen ? '12px' : '16px',
                lineHeight: '24px',
                color: '#1F2937',
                pointerEvents: 'none',
                minWidth: isSmallScreen ? '130px' : '280px',
                width: isSmallScreen ? '160px' : '280px',
                '& .MuiButton-label': {
                  whiteSpace: 'nowrap'
                }
              }}
            >
              {dateRangeActive
                ? `${dateRange.start?.format(isSmallScreen ? 'DD MMM' : 'ddd, DD MMM') ?? ''} – ${dateRange.end?.format(isSmallScreen ? 'DD MMM' : 'ddd, DD MMM') ?? ''}`
                : showSingleDay
                  ? `${selectedDate.format(isSmallScreen ? 'DD MMM' : 'ddd,DD MMM')}`
                  : `${selectedDate.format(isSmallScreen ? 'DD MMM' : 'ddd,DD MMM')} - ${weekEnd.format(isSmallScreen ? 'DD MMM' : 'ddd,DD MMM')}`}
            </Button>
            <IconButton
              onClick={() => handleDayShift('next')}
              disabled={dateRangeActive}
              size="small"
              sx={{
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                width: isSmallScreen ? 28 : 36,
                height: isSmallScreen ? 28 : 36,
                '&:hover': {
                  color: '#004DC2',
                  backgroundColor: '#F5F8FF',
                  borderColor: '#E0E0E0'
                }
              }}
            >
              <Typography component="span" sx={{ fontSize: isSmallScreen ? 16 : 20, lineHeight: 1 }}>
                &#8250;
              </Typography>
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: isSmallScreen ? 0.5 : 1.5 }}>
            <Badge color="primary" badgeContent={activeFilterCount} invisible={activeFilterCount === 0}>
              <Button
                variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                startIcon={<FilterAltOutlinedIcon sx={{ width: isSmallScreen ? 16 : 20, height: isSmallScreen ? 16 : 20 }} />}
                onClick={handleFilterButtonClick}
                sx={{
                  borderRadius: '8px',
                  px: isSmallScreen ? 1 : 2.5,
                  height: isSmallScreen ? 32 : 40,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: isSmallScreen ? '12px' : '14px',
                  backgroundColor: activeFilterCount > 0 ? '#0063F7' : '#fff',
                  color: activeFilterCount > 0 ? '#fff' : '#1F2937',
                  borderColor: '#E0E0E0',
                  '&:hover': {
                    color: activeFilterCount > 0 ? '#fff' : '#004DC2',
                    backgroundColor: activeFilterCount > 0 ? '#0056d6' : '#F5F8FF'
                  }
                }}
              >
                Filter
              </Button>
            </Badge>
          </Box>
        </Box>

        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { p: 2.5, width: 380, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' } } }}
        >
          <Stack spacing={2}>
            {/* Company Filter - Only show if user has multiple companies */}
            {(user?.hasMultipleCompanies !== false) && (
              <>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showCompanyOptions}
                          onChange={(e) => {
                            setShowCompanyOptions(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedCompanies([]);
                            }
                          }}
                        />
                      }
                      label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Company</Typography>}
                    />
                  </Box>
                  {showCompanyOptions && (
                    <Box sx={{ mt: 1.5, ml: 2 }}>
                      {companyOptions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No companies available.
                        </Typography>
                      ) : (
                        <Box sx={{ maxHeight: 180, overflowY: 'auto', pr: 1 }}>
                          <FormGroup>
                            {companyOptions.map((company) => (
                              <FormControlLabel
                                key={company}
                                control={
                                  <Checkbox
                                    checked={selectedCompanies.includes(company)}
                                    onChange={() => handleCompanyToggle(company)}
                                  />
                                }
                                label={company}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                              />
                            ))}
                          </FormGroup>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <Divider />
              </>
            )}

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showStatusOptions}
                      onChange={(e) => {
                        setShowStatusOptions(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedStatuses([]);
                        }
                      }}
                    />
                  }
                  label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Status</Typography>}
                />
              </Box>
              {showStatusOptions && (
                <Box sx={{ mt: 1.5, ml: 2 }}>
                  <FormGroup>
                    {['Expired', 'Active', 'New lead'].map((status) => (
                      <FormControlLabel
                        key={status}
                        control={
                          <Checkbox
                            checked={selectedStatuses.includes(status)}
                            onChange={() => handleStatusToggle(status)}
                          />
                        }
                        label={status}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Date range
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                <Button
                  variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')}
                  sx={{
                    flex: 1,
                    '&:hover': {
                      backgroundColor: showDateRangeCalendar === 'start' ? '#0056d6' : '#E0EBFF',
                      borderColor: '#E0EBFF'
                    }
                  }}
                >
                  {dateRange.start ? dateRange.start.format('DD MMM') : 'Start Date'}
                </Button>
                <Button
                  variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end')}
                  sx={{
                    flex: 1,
                    '&:hover': {
                      backgroundColor: showDateRangeCalendar === 'end' ? '#0056d6' : '#E0EBFF',
                      borderColor: '#E0EBFF'
                    }
                  }}
                  disabled={!Boolean(dateRange.start)}
                >
                  {dateRange.end ? dateRange.end.format('DD MMM') : 'End Date'}
                </Button>
              </Stack>
              {showDateRangeCalendar && (
                <Box
                  ref={dateRangeCalendarRef}
                  sx={{
                    position: 'relative',
                    zIndex: 1300,
                    boxShadow: 3,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    width: '100%',
                    overflow: 'hidden',
                    padding: '12px',
                    mb: 1.5,
                    minHeight: '320px',
                    maxHeight: '320px',
                    '& .react-calendar': {
                      width: '100%',
                      maxWidth: '100%',
                      minHeight: '280px',
                      '&__navigation': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 6px',
                        margin: '0 0 8px 0',
                        '&__label': {
                          minWidth: '180px',
                          textAlign: 'center',
                          flexGrow: 0,
                          fontWeight: '600 !important',
                          margin: 0,
                          padding: '8px 0',
                          fontSize: '16px',
                          lineHeight: '1.4'
                        },
                        '&__arrow': {
                          minWidth: '32px',
                          padding: '8px 2px',
                          margin: 0
                        }
                      },
                      '&__month-view__weekdays': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0 6px',
                        marginBottom: '4px',
                        '&__weekday': {
                          width: '36px',
                          padding: '4px 0',
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: '600 !important',
                          textTransform: 'uppercase',
                          color: '#666 !important',
                          backgroundColor: 'transparent !important',
                          border: 'none !important',
                          '& abbr': {
                            textDecoration: 'none',
                            display: 'inline-block',
                            width: '100%',
                            textAlign: 'center'
                          }
                        }
                      },
                      '&__month-view__days': {
                        display: 'grid !important',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        padding: '0 6px',
                        '&__day': {
                          width: '36px',
                          height: '36px',
                          margin: '0 auto',
                          fontWeight: '600 !important',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                          '&--neighboringMonth': {
                            opacity: 0.5
                          },
                          '&--weekend': {
                            color: '#212121 !important'
                          },
                          '&--now': {
                            fontWeight: 'bold',
                            color: '#004DC2 !important',
                            backgroundColor: 'transparent !important',
                            '&:enabled:hover': {
                              backgroundColor: '#E0EBFF !important'
                            }
                          }
                        }
                      }
                    },
                    '& .react-calendar__tile': {
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '13px',
                      color: '#212121 !important',
                      transition: 'all 0.2s ease',
                      '&:enabled:hover': {
                        backgroundColor: '#E0EBFF !important',
                        color: '#212121 !important'
                      },
                      '&--active, &--active:enabled, &--active:enabled:hover': {
                        backgroundColor: '#004DC2 !important',
                        color: '#fff !important',
                        borderRadius: '50%',
                        border: 'none',
                        boxShadow: 'none',
                        outline: 'none'
                      }
                    },
                    "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled": {
                      backgroundColor: '#004DC2 !important',
                      color: '#fff !important',
                    },
                    "& .react-calendar__month-view__days__day--weekend": {
                      color: '#212121 !important',
                    },
                    "& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover": {
                      backgroundColor: '#E0EBFF !important',
                    },
                    "& .react-calendar__navigation button": {
                      color: '#333 !important',
                      background: 'none !important',
                      fontWeight: '600 !important',
                      border: 'none !important',
                      outline: 'none !important',
                      '&:hover': {
                        backgroundColor: '#E0EBFF !important',
                        color: '#333 !important',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent !important',
                        outline: 'none !important',
                        boxShadow: 'none !important',
                      },
                      '&:active': {
                        backgroundColor: 'transparent !important',
                      },
                    },
                    "& .react-calendar__navigation__label": {
                      color: '#333 !important',
                      background: 'transparent !important',
                      fontWeight: '600 !important',
                      '&:hover': {
                        backgroundColor: 'transparent !important',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent !important',
                        outline: 'none !important',
                      },
                    },
                  }}
                >
                  {(() => {
                    try {
                      return (
                        <Calendar
                          value={showDateRangeCalendar === 'start' ? dateRange.start?.toDate() : dateRange.end?.toDate()}
                          onChange={handleDateRangeCalendarChange}
                          {...getCalendarConfig()}
                        />
                      );
                    } catch (error) {
                      console.error('Date range calendar rendering failed:', error);
                      return (
                        <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
                          Calendar could not be loaded.
                        </Box>
                      );
                    }
                  })()}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {showDateRangeCalendar ? `Select ${showDateRangeCalendar} date` : 'Click to select date range'}
              </Typography>
            </Box>

            <Divider />

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                onClick={clearFilters}
                size="small"
                color="inherit"
                sx={{
                  '&:hover': {
                    backgroundColor: '#F5F5F5'
                  }
                }}
              >
                Clear filters
              </Button>
              <Button onClick={handleFilterClose} size="small" variant="contained">
                Done
              </Button>
            </Stack>
          </Stack>
        </Popover>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: 'none',
            backgroundColor: 'transparent',
            borderRadius: 0,
            px: 0,
            mx: isMobile ? -0.5 : { xs: -2, sm: -3 }, // Extend table beyond container padding
            width: isMobile ? 'calc(100% + 8px)' : { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' }, // Compensate for negative margin
            maxHeight: 'calc(100vh - 300px)', // Fixed height to leave space for pagination
            overflow: 'auto', // Allow scrolling if content exceeds height
            '& .MuiTableHead-root .MuiTableCell-root': {
              borderBottom: '1px solid #E0E0E0',
              backgroundColor: 'transparent',
            },
            '& .MuiTableBody-root .MuiTableCell-root': {
              borderBottom: '1px solid #E0E0E0',
              backgroundColor: 'transparent',
            },
            '& .MuiTable-root': {
              borderLeft: 'none',
              borderRight: 'none',
            },
            '& .MuiTableRow-root': {
              borderRadius: '0 !important',
            },
            '& .MuiTableCell-root': {
              borderRadius: '0 !important',
            },
            '& .MuiPaper-root': {
              borderRadius: '0 !important',
              borderTopLeftRadius: '0 !important',
              borderTopRightRadius: '0 !important',
              borderBottomLeftRadius: '0 !important',
              borderBottomRightRadius: '0 !important',
              boxShadow: 'none',
              border: 'none',
            },
            '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
              boxShadow: 'none',
              borderBottom: '1px solid #E0E0E0',
            },
          }}
        >
          <Table sx={{
            borderCollapse: 'collapse',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            tableLayout: isMobile ? 'auto' : 'fixed',
            width: '100%',
            '& td:first-of-type, & th:first-of-type': {
              pl: isMobile ? 1 : 4,
            },
          }}>
            <TableHead sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
              <TableRow
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  '& > th': {
                    borderBottom: '1px solid rgba(224,224,224,0.5)',
                  }
                }}
              >
                <TableCell sx={{ borderBottom: 'none', backgroundColor: 'transparent', px: 0, width: isMobile ? 60 : '6%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 60, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <span style={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: isSmallScreen ? '12px' : '16px',
                      lineHeight: isSmallScreen ? '18px' : '24px',
                      letterSpacing: '0.01em',
                      color: '#4B5563DE',
                      opacity: 1,
                      whiteSpace: 'nowrap'
                    }}>#</span>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: 'none', backgroundColor: 'transparent', px: isMobile ? 1 : 2, width: isMobile ? 120 : '15%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <PersonIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                    <span style={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: isSmallScreen ? '12px' : '16px',
                      lineHeight: isSmallScreen ? '18px' : '24px',
                      letterSpacing: '0.01em',
                      color: '#4B5563DE',
                      opacity: 1,
                      whiteSpace: 'nowrap'
                    }}>Customer</span>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: 'none', backgroundColor: 'transparent', px: isMobile ? 1 : 2, width: isMobile ? 120 : '16%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <CategoryIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                    <span style={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: isSmallScreen ? '12px' : '16px',
                      lineHeight: isSmallScreen ? '18px' : '24px',
                      letterSpacing: '0.01em',
                      color: '#4B5563DE',
                      opacity: 1,
                      whiteSpace: 'nowrap'
                    }}>Category</span>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: 'none', backgroundColor: 'transparent', px: isMobile ? 1 : 2, width: isMobile ? 120 : '17%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                    <span style={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: isSmallScreen ? '12px' : '16px',
                      lineHeight: isSmallScreen ? '18px' : '24px',
                      letterSpacing: '0.01em',
                      color: '#4B5563DE',
                      opacity: 1,
                      whiteSpace: 'nowrap'
                    }}>Policy Type</span>
                  </Box>
                </TableCell>
                {showExpiryColumn && (
                  <TableCell sx={{ borderBottom: 'none', backgroundColor: 'transparent', px: isMobile ? 1 : 2, width: isMobile ? 120 : '14%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 160, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      <AccessTimeIcon sx={{ width: 16, height: 16, color: '#0000008A' }} />
                      <span style={{
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: isSmallScreen ? '12px' : '16px',
                        lineHeight: isSmallScreen ? '18px' : '24px',
                        letterSpacing: '0.01em',
                        color: '#4B5563DE',
                        opacity: 1,
                        whiteSpace: 'nowrap'
                      }}>Expiry Date</span>
                    </Box>
                  </TableCell>
                )}
                {showStatusColumn && (
                  <TableCell sx={{ borderBottom: 'none', backgroundColor: 'transparent', px: isMobile ? 1 : 2, width: isMobile ? 120 : '14%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      <FlagIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                      <span style={{
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: isSmallScreen ? '12px' : '16px',
                        lineHeight: isSmallScreen ? '18px' : '24px',
                        letterSpacing: '0.01em',
                        color: '#4B5563DE',
                        opacity: 1,
                        whiteSpace: 'nowrap'
                      }}>Status</span>
                    </Box>
                  </TableCell>
                )}
                <TableCell sx={{ borderBottom: 'none', backgroundColor: 'transparent', px: isMobile ? 1 : 2, width: isMobile ? 120 : '18%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <span style={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: isSmallScreen ? '12px' : '16px',
                      lineHeight: isSmallScreen ? '18px' : '24px',
                      letterSpacing: '0.01em',
                      color: '#4B5563DE',
                      opacity: 1,
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}><MoreVertIcon sx={{ width: isSmallScreen ? 15 : 24, height: isSmallScreen ? 15 : 24, color: '#4B5563DE' }} /> Actions</span>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPolicies.length > 0 ? (
                paginatedPolicies.map((p, index) => {
                  // Calculate status using the utility function
                  const statusText = calculatePolicyStatus(p.expiryDate, p.status);
                  // Calculate serial number
                  const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
                  const parsedExpiry = parseDate(p.expiryDate);
                  const expiryDisplay = parsedExpiry && !isNaN(parsedExpiry.getTime())
                    ? dayjs(parsedExpiry).format('DD MMM')
                    : '–';

                  return (
                    <TableRow
                      key={p.id}
                      hover
                      onClick={() => navigate(`/policy/${p.id}`, { state: { policy: p, fromScreen: 'renewals', fromPage: currentPage, selectedDate: selectedDate.format('YYYY-MM-DD') } })}
                      sx={{
                        height: isTinyScreen ? '60px' : '76px',
                        cursor: 'pointer',
                        '& > td': {
                          height: isTinyScreen ? '60px' : '76px',
                          padding: isTinyScreen ? '8px 12px' : '10px 16px',
                          borderBottom: '1px solid rgba(224,224,224,0.5)',
                          backgroundColor: 'transparent',
                          verticalAlign: 'middle',
                        },
                        '&:hover': {
                          backgroundColor: '#E0EBFF !important'
                        }
                      }}
                    >
                      <TableCell sx={{ px: 0, width: isMobile ? 60 : '8%' }}>
                        <div style={{
                          fontFamily: 'Roboto',
                          fontWeight: 400,
                          fontSize: isTinyScreen ? '13px' : '16px',
                          lineHeight: isTinyScreen ? '20px' : '24px',
                          letterSpacing: '0.02em',
                          color: '#212121',
                          height: '48px',
                          width: '100%',
                          maxWidth: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          {serialNumber}
                        </div>
                      </TableCell>
                      <TableCell sx={{ px: isMobile ? 1 : 2, width: isMobile ? 120 : '16.66%' }}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontFamily: 'Roboto',
                          fontWeight: 400,
                          fontSize: isTinyScreen ? '13px' : '16px',
                          lineHeight: isTinyScreen ? '20px' : '24px',
                          letterSpacing: '0.02em',
                          color: '#212121',
                          height: '48px',
                          width: '100%',
                          maxWidth: '100%',
                        }}>
                          {get(p, "name", "clientName", "customerName", "policyholderName") || "–"}
                          <br />
                          <span style={{ color: '#757575', fontSize: isTinyScreen ? '12px' : '14px', fontWeight: 400 }}>
                            {get(p, "mobile", "clientPhone", "phone") || ""}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell sx={{ px: isMobile ? 1 : 2, width: isMobile ? 120 : '16.66%' }}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                          fontFamily: 'Roboto',
                          fontWeight: 400,
                          fontSize: isTinyScreen ? '13px' : '16px',
                          lineHeight: isTinyScreen ? '20px' : '24px',
                          letterSpacing: '0.02em',
                          color: '#212121',
                          height: '48px',
                        }}>
                          {p.policyCategoryType || "–"}
                        </div>
                        {/* Category row: vehicleRegistration not shown */}
                      </TableCell>
                      <TableCell sx={{ px: isMobile ? 1 : 2, width: isMobile ? 120 : '16.66%' }}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                          fontFamily: 'Roboto',
                          fontWeight: 400,
                          fontSize: isTinyScreen ? '13px' : '16px',
                          lineHeight: isTinyScreen ? '20px' : '24px',
                          letterSpacing: '0.02em',
                          color: '#212121',
                          height: '48px',
                        }}>
                          {p.policyType || "–"}
                        </div>
                      </TableCell>
                      {showExpiryColumn && (
                        <TableCell sx={{ px: isMobile ? 1 : 2, width: isMobile ? 120 : '13%' }}>
                          <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: isTinyScreen ? '13px' : '16px',
                            lineHeight: isTinyScreen ? '20px' : '24px',
                            letterSpacing: '0.02em',
                            color: '#212121',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            {expiryDisplay}
                          </div>
                        </TableCell>
                      )}
                      {showStatusColumn && (
                        <TableCell sx={{ px: isMobile ? 1 : 2, width: isMobile ? 120 : '16.66%' }}>
                          <StatusBadge status={statusText} size={isSmallScreen ? 'small' : 'normal'} />
                        </TableCell>
                      )}
                      <TableCell sx={{ px: isMobile ? 1 : 2, width: isMobile ? 120 : '16.66%' }}>
                        <div
                          onClick={(e) => handleRemindClick(p, e)}
                          role="button"
                          tabIndex={0}
                          aria-label="Send Remainder"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRemindClick(p, e);
                            }
                          }}
                          style={{
                            height: '28px',
                            minWidth: isSmallScreen ? '90px' : '130px',
                            maxWidth: isSmallScreen ? '90px' : '130px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            borderRadius: '12px',
                            padding: isSmallScreen ? '4px 8px' : '4px 12px',
                            backgroundColor: 'transparent',
                            border: '1px solid #166434',
                            outline: 'none',
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#E6F9EB';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <NotificationsIcon sx={{ width: 16, height: 16, color: '#166434' }} />
                          <span
                            style={{
                              color: '#166434',
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              letterSpacing: '0.01em',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {isSmallScreen ? 'Remind' : 'Reminder'}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow
                  sx={{
                    height: '76px',
                    '& > td': {
                      height: '76px',
                      padding: '10px 16px',
                      borderBottom: 'none',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <TableCell colSpan={totalColumnCount} align="center">
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '24px',
                        letterSpacing: '0.02em',
                        color: '#6B7280',
                        mb: 1
                      }}>
                        No upcoming renewals. Great time to follow up with new leads!
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}


            </TableBody>
          </Table>
        </TableContainer>

        {/* Spacer to push pagination to bottom */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Pagination moved below */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 'auto', // Push pagination to bottom
              pt: 1,
              pb: 1,
              backgroundColor: '#fff',
              borderRadius: '0',
              boxShadow: 'none'
            }}
          >
            <Pagination
              count={Math.max(totalPages, 1)}
              page={currentPage}
              onChange={(_, value) => setCurrentPage(value)}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '50%',
                  border: '1px solid #D1D5DB',
                  width: 36,
                  height: 36,
                  margin: '0 4px',
                  color: '#6B7280',
                  '&:hover': {
                    backgroundColor: '#E0EBFF',
                  },
                },
                '& .MuiPaginationItem-previousNext': {
                  borderRadius: 0,
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: 'auto',
                  height: 'auto',
                  margin: '0 4px',
                  color: '#6B7280',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .Mui-selected': {
                  backgroundColor: '#004DC2 !important',
                  color: '#fff !important',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: '#004DC2 !important',
                  },
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <UploadPolicyModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onExtracted={(data) => {
          setPrefillData(data);
          if (data.rawFile) {
            setPdfFile(data.rawFile);
          }
          setShowUploadModal(false);
          setShowModal(true);
        }}
        user={user}
        setShowSuccessDialog={setShowSuccessDialog}
        setPdfUrl={setSuccessPdfUrl}
      />
      {
        showModal && (
          <UnifiedPolicyForm
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              setPrefillData(null);
              setPdfFile(null);
            }}
            prefillData={prefillData}
            pdfFile={pdfFile}
            onSuccess={(pdfUrl, formData) => {
              // Note: Policy is already saved in UnifiedPolicyForm with setDoc()
              // Don't dispatch addPolicy again to avoid duplicate records

              // Increment daily policy counter after successful save
              try {
                incrementDailyPolicyCounter(user.uid);
                console.log('📊 Daily policy counter incremented after successful save');
              } catch (error) {
                console.error('❌ Error incrementing daily policy counter:', error);
                // Don't block the success flow if counter fails
              }

              // Mark ALL screens for cache invalidation (Renewals write operation)
              firebaseCache.invalidateAfterWrite('renewals_write', user.uid);

              // Re-fetch policies to ensure Redux state is updated for the report
              if (isAdmin && userIds.length > 0) {
                dispatch(fetchPoliciesForMultipleUsers(userIds));
              } else if (user?.uid && !isAdmin) {
                dispatch(fetchPolicies(user.uid));
              }

              if (pdfUrl) setSuccessPdfUrl(pdfUrl);
              if (formData) {
                setSuccessPremium(formData.amountPayable);
                setSuccessBalance(Math.max((Number(formData.amountPayable) || 0) - (Number(formData.paidAmount) || 0), 0));
              }
              setShowSuccessDialog(true);
            }}
          />
        )
      }
      <SuccessDialog
        isOpen={showSuccessDialog}
        pdfUrl={successPdfUrl}
        headerText="Policy Added Successfully"
        messageText="Policy has been successfully added. Share with customer via WhatsApp."
        agentName={user?.displayName || "Your Agent"}
        premiumAmount={successPremium}
        balanceAmount={successBalance}
        onClose={() => setShowSuccessDialog(false)}
      />
      <RemindDialog
        isOpen={remindDialogOpen}
        onClose={handleRemindClose}
        onConfirm={handleRemindConfirm}
        message="Send renewal reminder via WhatsApp?"
      />

      <ShareReportDialog
        isOpen={showShareReport}
        onClose={() => setShowShareReport(false)}
        user={user}
        policies={policies}
        isAdmin={isAdmin}
      />

    </Box >
  );
};

export default Renewals;
